import { of } from 'rxjs'
import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { Application } from 'src/app/account/services/application.service'
import {
  AccessTemplatesDto,
  ApplicationType,
  RecoveryPointDto,
  SyncPairAccessDto,
  SyncPhaseDto,
} from 'src/app/services/dtos'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { ResourceType, getResourceTypeDetails } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'

function buildMockRecoveryPoints(includeIgw: boolean, includeSg: boolean, includeVnet: boolean): RecoveryPointDto[] {
  const now = Date.now()
  const hour = 60 * 60 * 1000
  const resourceTypeStats: Array<{ resourceType: ResourceType }> = []
  if (includeIgw) resourceTypeStats.push({ resourceType: ResourceType.ec2InternetGateway })
  if (includeSg) resourceTypeStats.push({ resourceType: ResourceType.ec2SecurityGroup })
  if (includeVnet) resourceTypeStats.push({ resourceType: ResourceType.azureVirtualNetwork })

  // 8 RPs across the last 24h, with 2 protected (locked) so the
  // "Restrict to indefinitely-retained" toggle has rows to filter to.
  return [0, 1, 3, 6, 9, 12, 18, 24].map((hoursAgo, idx) => ({
    recoveryPointId: `mock-rp-${idx}`,
    timestamp: new Date(now - hoursAgo * hour),
    protected: idx === 0 || idx === 4,
    stats: { resourceTypeStats } as any,
  })) as RecoveryPointDto[]
}

export interface MockApplicationOptions {
  applicationId?: string
  name?: string
  type?: ApplicationType
  syncPhase?: SyncPhaseDto
  rpo?: number
  networkSandboxEnabled?: boolean
  advancedNetworkSandbox?: boolean
  withIgw?: boolean
  withSg?: boolean
  withVnet?: boolean
  // Region overrides. The launch dialogs scope their candidate app list to
  // apps sharing the anchor's source+target region, so giving each sandbox
  // scenario a unique region pair isolates it — otherwise a FAILOVER_TEST app
  // in the shared region would flip sandboxFrozen on unrelated scenarios.
  sourceRegion?: string
  targetRegion?: string
  // Resources returned by getRestoreResources(). The unquarantine dialog reads
  // these and shows the ones in a quarantine status; empty by default.
  restoreResources?: ViewableApplicationResource[]
  // Restore-completion state. When unset, a FAILOVER_TEST app reads as still
  // restoring ("Awaiting restore…"), which the conclude-test dialog buckets as
  // cancel-bound. Set these to make the app read as "restore complete" so the
  // full conclude flow (test outcome + generate report) is reachable.
  lastRestoreComplete?: Date
  lastRestoreSuccess?: Date
}

/**
 * Returns an `Application`-shaped object suitable for mounting any of the
 * launch / conclude / failback / unquarantine dialogs without a real backend.
 * Cast as `Application` — only the fields read by those dialogs are populated.
 */
export function buildMockApplication(opts: MockApplicationOptions = {}): Application {
  const {
    applicationId = `mock-app-${Math.random().toString(36).slice(2, 8)}`,
    name = 'Mock application',
    type = ApplicationType.AWS,
    syncPhase = SyncPhaseDto.STANDBY,
    rpo = 60,
    networkSandboxEnabled = false,
    advancedNetworkSandbox = false,
    withIgw = type === ApplicationType.AWS,
    withSg = type === ApplicationType.AWS,
    withVnet = type === ApplicationType.AZURE,
    sourceRegion = type === ApplicationType.AZURE ? 'eastus' : 'us-east-1',
    targetRegion = type === ApplicationType.AZURE ? 'westus' : 'us-west-2',
    restoreResources = [],
    lastRestoreComplete,
    lastRestoreSuccess,
  } = opts

  const recoveryPoints = buildMockRecoveryPoints(withIgw, withSg, withVnet)
  const cloud = type === ApplicationType.AZURE ? 'azure' : 'aws'

  const app: Partial<Application> & Record<string, unknown> = {
    applicationId,
    name,
    type,
    syncPhase,
    rpo,
    accountId: 'MOCK-TEST-account-id',
    sourceRegion,
    targetRegion,
    sourceAwsAccountId: '111111111111',
    targetAwsAccountId: '222222222222',
    sourceEndpointUri: `${cloud}:111111111111:${sourceRegion}`,
    targetEndpointUri: `${cloud}:222222222222:${targetRegion}`,

    // Recovery points + the loader the dialog subscribes to in ngOnInit.
    recoveryPoints,
    refreshRecoveryPoints$: () => of(null),

    // Sandbox-related getters are read directly off the application.
    networkSandboxEnabled,
    networkSandboxWhitelist: [],
    networkSandboxAllowedDomains: [],
    networkSandboxAllowedServices: [],
    advancedNetworkSandboxEnabled: advancedNetworkSandbox,

    // Methods occasionally hit by issue components nested in dialogs.
    getSyncPairResourceConfig$: () => of(null),
    manualResourceTemplateUrl$: of(''),

    // Restore-scope resources the unquarantine dialog reads (it filters these
    // to the ones in a quarantine status). Empty unless the scenario seeds a
    // set. updateRestoreResourceStatus is stubbed so the dialog's optimistic
    // post-unquarantine update doesn't crash.
    // Cast: mkRecoveryResource yields ViewableApplicationResource POJOs, which
    // aren't structurally assignable to the ApplicationResource class the real
    // getRestoreResources returns. The dialog only reads plain fields off them.
    getRestoreResources: (_filter?: string) => restoreResources as any,
    updateRestoreResourceStatus: (_status: any, _arns: any) => {},

    currentlyRestoring: false,
    lastRestoreComplete,
    lastRestoreSuccess,
    identityProviderId: null,
  }

  return app as unknown as Application
}

// Mock SyncPairAccessDto for the "Let's get you updated" (update-access)
// dialog. Defaults to "out of date" — sourceVersion < latest, so the dialog
// shows the upgrade affordance instead of an all-good empty state. This is also
// the trigger state for the legacy-template advisory on the connect page, which
// needs managedByStackSet when previewing the StackSets flow.
// `unknownVersion` mirrors Azure, where the backend has no access-template version
// and reports -1 while deriving "not latest" from the granted change set instead.
export function buildMockSyncPairAccess(
  options: { managedByStackSet?: boolean; unknownVersion?: boolean } = {}
): SyncPairAccessDto {
  const managedByStackSet = options.managedByStackSet ?? false
  const version = options.unknownVersion ? -1 : 12
  return {
    sourceAccess: true,
    sourceConfigValid: true,
    sourceVersion: version,
    sourceIsLatest: false,
    sourceCloudFormationAccessStackName: 'arpio-source-access',
    sourceTemplateManagedByStackSet: managedByStackSet,
    targetAccess: true,
    targetConfigValid: true,
    targetVersion: version,
    targetIsLatest: false,
    targetCloudFormationAccessStackName: 'arpio-target-access',
    targetTemplateManagedByStackSet: managedByStackSet,
    failbackAccess: true,
    failbackConfigValid: true,
    failbackVersion: version,
    failbackIsLatest: false,
    failbackCloudFormationAccessStackName: 'arpio-failback-access',
    failbackTemplateManagedByStackSet: managedByStackSet,
    missingChanges: options.unknownVersion
      ? [{ key: 'azure_netapp_files', description: 'Azure NetApp Files support' }]
      : undefined,
  }
}

// Mock AccessTemplatesDto so the dialog finishes loading and renders the
// inner aws-account-access-view rows with stack templates instead of the
// gray skeleton placeholders. `includeStackSets` is opt-in because
// UpdateAccessDialogComponent.stackSetsEnabled is derived purely from the
// presence of these URLs, so an unconditional block would flip that dialog's
// preview from a CloudFormation account to a StackSets one. The StackSets flow
// needs them or its copy-paste CLI blocks interpolate `undefined`.
export function buildMockAccessTemplates(
  options: { includeStackSets?: boolean; includeAzure?: boolean } = {}
): AccessTemplatesDto {
  const templates: AccessTemplatesDto = {
    sourceTemplateS3Url: 'https://example.s3.amazonaws.com/source.yml',
    targetTemplateS3Url: 'https://example.s3.amazonaws.com/target.yml',
    failbackTemplateS3Url: 'https://example.s3.amazonaws.com/failback.yml',
  }
  if (options.includeStackSets) {
    templates.stackSetsTemplate = {
      sourceStackSetTemplateS3Url: 'https://example.s3.amazonaws.com/source-stackset.yml',
      targetStackSetTemplateS3Url: 'https://example.s3.amazonaws.com/target-stackset.yml',
      sourceTemplate: { bucket: 'example-bucket', key: 'source-template.yml' },
      targetTemplate: { bucket: 'example-bucket', key: 'target-template.yml' },
    }
  }
  // azure-access-view renders an install command, not a template URL, so without
  // these its command block is empty.
  if (options.includeAzure) {
    templates.sourceInstallCommand =
      'az deployment sub create --location eastus --template-uri https://example.blob.core.windows.net/source.json'
    templates.targetInstallCommand =
      'az deployment sub create --location westus2 --template-uri https://example.blob.core.windows.net/target.json'
  }
  return templates
}

// ──────────────────────────────────────────────────────────────────────────
//  StackSets regeneration-job fixtures
// ──────────────────────────────────────────────────────────────────────────

// Visual-only flags driving the tab strip + action buttons surfaced above the
// composed screen. Mirrors the production gating in application.component.html
// tab-bar-actions + the `disabledActionReason` getter in application.component.ts.
// Kept as plain values so the test page doesn't have to invoke the real
// `entitledForCapability` / `canRecover` / `lastStagingSuccess` pipelines.
export interface RecoveryScreenTabBarFlags {
  // Mirrors ApplicationComponent.disabledActionReason. Per-phase buttons always
  // render; when this is non-null every action is disabled and shows this string
  // as its tooltip (a failback/restore in flight, or a conclude-to-standby
  // wind-down). null = actions enabled. Replaces the old spinner-collapse model.
  disabledActionReason: string | null
  showSandboxSettings: boolean
  // STANDBY-phase actions
  showTestButton: boolean
  showRecoverButton: boolean
  // STANDBY disabled when no successful backup has landed yet.
  testRecoverDisabled: boolean
  // FAILOVER-phase actions
  showUnquarantine: boolean
  showFailback: boolean
  // FAILBACK disabled until backend reports failback resources.
  failbackDisabled: boolean
  showConcludeRecovery: boolean
  // FAILOVER_TEST-phase action
  showConcludeTest: boolean
  // Toggles label between CONCLUDE TEST and CANCEL TEST (early stop).
  isEarlyStopTest: boolean
}

interface MockResourceOpts {
  name: string
  type: ResourceType
  status: ResourceStatus
  // Recovery-context defaults: source resource name visible, target name
  // mirrors source. Override targetName='' to render a pilot-light row
  // (source visible, target column empty). Override sourceName='' to render
  // an orphaned-target row.
  sourceName?: string
  targetName?: string
  progress?: number
  message?: string
  substatus?: string
  parentArn?: string
  arn?: string
}

// ViewableApplicationResource POJO factory mirroring the construction-time
// substatus rules from ApplicationResource: pilot light gets the
// "Pilot-light resource…" hint, orphaned target gets "Not in the current
// recovery point", PENDING with confirming-substatus, NOT_PROCESSED with
// "Upstream dependency failed". Substatus is otherwise derived from
// computeResourceSubstatus's rules — we precompute it here rather than at
// runtime because the POJO doesn't invoke that helper.
export function mkRecoveryResource(opts: MockResourceOpts): ViewableApplicationResource {
  const {
    name,
    type,
    status,
    sourceName = name,
    targetName = name,
    progress = 0,
    message = null,
    substatus,
    parentArn,
    arn,
  } = opts

  const computedSubstatus = (() => {
    if (substatus !== undefined) return substatus
    // Pilot-light: source visible, target blank, NO_STATUS — matches isPilotLight().
    if (status === ResourceStatus.SUCCEEDED && sourceName && !targetName) {
      return 'Pilot-light resource — created on demand during recovery'
    }
    // Orphaned target: target visible, source blank, NO_STATUS.
    if (status === ResourceStatus.NO_STATUS && !sourceName && targetName) {
      return 'Not in the current recovery point'
    }
    switch (status) {
      case ResourceStatus.PROCESSING:
        return progress > 0 && progress < 1 ? `${Math.round(progress * 100)}%` : message || undefined
      case ResourceStatus.PENDING:
        return message || 'Waiting in queue...'
      case ResourceStatus.UNSATISFIED_DEPENDENCY:
        return message || 'Waiting for dependency'
      case ResourceStatus.NOT_PROCESSED:
        return 'Upstream dependency failed'
      case ResourceStatus.FAILED:
        return message || undefined
      default:
        return undefined
    }
  })()

  return {
    resourceType: type,
    resourceVariant: null,
    resourceArn: (arn ?? `arn:aws:MOCK-TEST:us-east-1:123456789012:${type}/${name}`) as Arn,
    sourceResourceName: sourceName,
    targetResourceName: targetName,
    resourceProperties: [],
    status,
    progress,
    message,
    substatus: computedSubstatus,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(type, null),
    resourceTypeDetails: getResourceTypeDetails({ resourceType: type }),
    parentArn,
  } as ViewableApplicationResource
}

// disabledActionReason tooltip strings — copied verbatim from
// ApplicationComponent.disabledActionReason / concludingToStandbyTooltip so the
// preview shows the exact production copy when an action is disabled.
export const ACTION_DISABLED_FAILBACK_IN_PROGRESS =
  'A failback is in progress — actions are unavailable until it completes.'

export const ACTION_DISABLED_CONCLUDING_TO_STANDBY =
  'This application is being restored back to standby — this action will be available once the restore completes.'

export const ACTION_DISABLED_RESTORE_IN_PROGRESS =
  'A restore is in progress — actions are unavailable until it completes.'

// Shown only when appGroupRestore is NOT entitled — a STANDBY refresh restore then
// disables the buttons. With the entitlement (the standard case) buttons stay enabled.
export const ACTION_DISABLED_STANDBY_REFRESH =
  'A standby refresh is in progress — actions are unavailable until it completes.'

// Failback button's own disabled tooltip (no failback-supporting resources),
// shown only when disabledActionReason is null. Mirrors failbackButtonDisabledTooltip.
export const FAILBACK_NO_RESOURCES_TOOLTIP = 'No resources in this recovery point support failback'

// Azure-only: a region-pair restore is still running for sibling apps, so this app
// can read 100% while its conclude/cancel buttons stay disabled.
export const ACTION_DISABLED_AZURE_REGION_PAIR =
  'A recovery is still in progress for other apps with the same primary and recovery regions. This action will be available once all of its applications finish restoring.'

// Tab-bar-flag presets per phase. Each call returns a fresh object so
// scenarios can splat-override individual flags without mutating siblings.
// `disabledActionReason` mirrors application.component.ts: per-phase buttons
// stay rendered during in-flight ops and are disabled with a tooltip rather
// than collapsing the row to a spinner. STANDBY refresh restores keep buttons
// enabled when appGroupRestore is entitled (the standard case), so the default
// is null — pass a reason only for FAILOVER restore / failback / conclude.
export function tabBarStandby(
  opts: { stagingSuccess?: boolean; appIsDeleted?: boolean; disabledActionReason?: string | null } = {}
): RecoveryScreenTabBarFlags {
  const { stagingSuccess = true, appIsDeleted = false, disabledActionReason = null } = opts
  return {
    disabledActionReason,
    showSandboxSettings: false,
    showTestButton: !appIsDeleted,
    showRecoverButton: !appIsDeleted,
    testRecoverDisabled: !stagingSuccess,
    showUnquarantine: false,
    showFailback: false,
    failbackDisabled: false,
    showConcludeRecovery: false,
    showConcludeTest: false,
    isEarlyStopTest: false,
  }
}

export function tabBarFailover(
  opts: {
    quarantined?: boolean
    hasFailbackResources?: boolean
    showFailback?: boolean
    disabledActionReason?: string | null
  } = {}
): RecoveryScreenTabBarFlags {
  const { quarantined = false, hasFailbackResources = true, showFailback = true, disabledActionReason = null } = opts
  return {
    disabledActionReason,
    showSandboxSettings: false,
    showTestButton: false,
    showRecoverButton: false,
    testRecoverDisabled: false,
    showUnquarantine: quarantined,
    // Production gates failback on entitled + !quarantined + canRecover. In the
    // quarantined scenario it disappears entirely (UNQUARANTINE takes its slot).
    showFailback: showFailback && !quarantined,
    failbackDisabled: !hasFailbackResources,
    showConcludeRecovery: true,
    showConcludeTest: false,
    isEarlyStopTest: false,
  }
}

export function tabBarFailoverTest(
  opts: { sandboxEnabled?: boolean; isEarlyStop?: boolean; disabledActionReason?: string | null } = {}
): RecoveryScreenTabBarFlags {
  const { sandboxEnabled = false, isEarlyStop = false, disabledActionReason = null } = opts
  return {
    // FAILOVER_TEST keeps its buttons enabled during a restore — only a failback
    // in flight disables them (the conclude dialog handles cancel-vs-conclude).
    disabledActionReason,
    showSandboxSettings: sandboxEnabled,
    showTestButton: false,
    showRecoverButton: false,
    testRecoverDisabled: false,
    showUnquarantine: false,
    showFailback: false,
    failbackDisabled: false,
    showConcludeRecovery: false,
    showConcludeTest: true,
    isEarlyStopTest: isEarlyStop,
  }
}
